// Error State Component
// Displays friendly error messages with retry functionality

import { AlertTriangle, RefreshCw, Home, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const ERROR_TYPES = {
  network: {
    icon: WifiOff,
    title: "Connection Lost",
    message: "We couldn't reach our servers. Please check your internet connection and try again.",
    showRetry: true,
    showHome: true
  },
  notFound: {
    icon: AlertTriangle,
    title: "Not Found",
    message: "We couldn't find what you're looking for. It may have expired or been moved.",
    showRetry: false,
    showHome: true
  },
  serverError: {
    icon: AlertTriangle,
    title: "Something Went Wrong",
    message: "Our servers are having a moment. Please try again in a few seconds.",
    showRetry: true,
    showHome: true
  },
  expired: {
    icon: AlertTriangle,
    title: "Session Expired",
    message: "Your session has timed out. Please start a new assessment.",
    showRetry: false,
    showHome: true
  },
  generic: {
    icon: AlertTriangle,
    title: "Oops!",
    message: "Something unexpected happened. Please try again.",
    showRetry: true,
    showHome: true
  }
};

export function ErrorState({ 
  type = "generic",
  customTitle,
  customMessage,
  onRetry,
  showRetry = true,
  showHome = true,
  className = ""
}) {
  const navigate = useNavigate();
  const config = ERROR_TYPES[type] || ERROR_TYPES.generic;
  const Icon = config.icon;
  
  const title = customTitle || config.title;
  const message = customMessage || config.message;
  const shouldShowRetry = showRetry && (config.showRetry || onRetry);
  const shouldShowHome = showHome && config.showHome;

  return (
    <div className={`flex flex-col items-center justify-center p-8 text-center ${className}`}>
      {/* Icon */}
      <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
      </div>
      
      {/* Title */}
      <h2 className="text-xl font-bold text-cipher-charcoal dark:text-white mb-2">
        {title}
      </h2>
      
      {/* Message */}
      <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm">
        {message}
      </p>
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
        {shouldShowRetry && onRetry && (
          <Button
            onClick={onRetry}
            className="flex-1 bg-cipher-green hover:bg-cipher-green-dark text-white"
            data-testid="error-retry-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        )}
        
        {shouldShowHome && (
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="flex-1 border-gray-300 dark:border-gray-600"
            data-testid="error-home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        )}
      </div>
    </div>
  );
}

// Inline error for forms
export function InlineError({ message, onRetry, className = "" }) {
  if (!message) return null;
  
  return (
    <div className={`flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${className}`}>
      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
      <span className="text-sm text-red-600 dark:text-red-400 flex-1">{message}</span>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export default ErrorState;
